import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { BERLIN_TIMEZONE } from '@pflegia/types/common/constants/date-time';
import { ReactComponent as ArrowBack } from '@pflegia/ui/icons/arrow-back.svg';
import { BLOG_ARTICLE } from '@pflegia/ui/pflegia/constants/routes';
import Button from '@pflegia/ui/shared/button';

import { PostType } from '@shared/components/magazin/types';

import { LOCALES } from '../../../constants/locale';

interface RelevantArticleCardProps {
  post: PostType;
}

const RelevantArticleCard: React.FC<RelevantArticleCardProps> = ({ post }) => {
  const { locale = LOCALES[0] } = useRouter();

  const { t } = useTranslation('magazin');
  const articleLink = `${BLOG_ARTICLE}/${post.slug}`;

  return (
    <div
      key={post.id}
      data-testid={`post-card-${post.id}`}
      className='group flex h-full w-full flex-col rounded-2xl border border-blue-100 p-4'>
      <div className='relative h-40 w-full shrink-0 overflow-hidden rounded-lg md:h-45'>
        {post.featureImage && (
          <Link href={articleLink}>
            <span className='sr-only'>{t('readArticle', { title: post.title })}</span>
            <Image
              data-testid={`post-image-${post.id}`}
              src={post.featureImage}
              alt={post.featureImageAlt || post.title}
              fill
              className='object-cover object-center'
              unoptimized
            />
          </Link>
        )}
      </div>

      <div className='flex flex-1 flex-col pt-4'>
        <div className='min-h-7 overflow-hidden'>
          <Link href={articleLink}>
            <h2
              className='line-clamp-3 text-base leading-7 font-medium text-blue-950 hover:underline'
              data-testid={`post-title-${post.id}`}>
              {post.title}
            </h2>
          </Link>
        </div>

        <div className='mt-auto flex items-center justify-between pt-4'>
          {post.publishedAt && (
            <span className='text-sm text-black' data-testid={`post-date-${post.id}`}>
              {new Date(post.publishedAt).toLocaleDateString(locale, { timeZone: BERLIN_TIMEZONE })}
            </span>
          )}
          <Link className='ml-auto' href={articleLink}>
            <Button
              className='text-primary cursor-pointer bg-transparent p-0! text-sm hover:p-0! hover:shadow-none!'
              data-testid={`read-more-button-${post.id}`}>
              <div className='flex items-center'>
                <span>{t('continueReading')}</span>
                <ArrowBack
                  aria-hidden='true'
                  className='ml-2 hidden h-4 w-4 -translate-x-1 rotate-180 opacity-0 transition-all duration-200 ease-in-out group-hover:flex group-hover:translate-x-0 group-hover:opacity-100'
                />
              </div>
              <span className='sr-only'>: {post.title}</span>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RelevantArticleCard;
